import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import { Landmark, ChevronRight, RefreshCw, Download, Loader2, AlertTriangle, Filter } from 'lucide-react';

import {
    fetchFDFinancialYears, fetchFDBankList, fetchFDNumbers, fetchFDReport,
    clearFDReport, clearFDNumbers,
    selectFDYears, selectFDYearsLoading, selectFDBankList, selectFDBankListLoading,
    selectFDNumbers, selectFDNumbersLoading, selectFDReport, selectFDReportLoading, selectFDReportError,
} from '../../slices/ReportsSlice/fdReportSlice';

const inputCls = 'w-full px-2.5 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent';

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const fmt = (v) => {
    const n = parseFloat(v);
    if (!n) return '0.00';
    return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const FDReport = () => {
    const dispatch = useDispatch();
    const years = useSelector(selectFDYears);
    const yearsLoading = useSelector(selectFDYearsLoading);
    const bankList = useSelector(selectFDBankList);
    const bankListLoading = useSelector(selectFDBankListLoading);
    const fdNumbers = useSelector(selectFDNumbers);
    const fdNumbersLoading = useSelector(selectFDNumbersLoading);
    const report = useSelector(selectFDReport);
    const reportLoading = useSelector(selectFDReportLoading);
    const reportError = useSelector(selectFDReportError);

    const [bankId, setBankId] = useState('');
    const [fdId, setFdId] = useState('');
    const [year, setYear] = useState('');
    const [month, setMonth] = useState('');
    const [ranParams, setRanParams] = useState(null);

    useEffect(() => {
        dispatch(fetchFDFinancialYears());
        dispatch(fetchFDBankList('0'));
    }, [dispatch]);

    useEffect(() => { if (reportError) toast.error(reportError); }, [reportError]);

    const yearOptions = useMemo(() => [{ Year: 'Any', YearValue: 'Any Year' }, ...years], [years]);
    const bankOptions = useMemo(() => [{ Bank_Id: 'SelectAll', Bank_Name: 'Select All' }, ...bankList], [bankList]);
    const fdOptions = useMemo(() => [{ FdID: 'SelectAll', FdNo: 'Select All' }, ...fdNumbers], [fdNumbers]);

    const selectedBankLabel = bankOptions.find((b) => String(b.Bank_Id) === String(bankId))?.Bank_Name || '';
    const selectedFdLabel = fdOptions.find((f) => String(f.FdID) === String(fdId))?.FdNo || '';
    const showMonth = fdId !== '' && selectedFdLabel !== 'Select All';

    const handleBankChange = (val) => {
        setBankId(val);
        setFdId(''); setMonth('');
        dispatch(clearFDNumbers());
        if (val !== '') {
            const numericBankId = val === 'SelectAll' ? 0 : val;
            dispatch(fetchFDNumbers(numericBankId));
        }
    };

    const handleFdChange = (val) => {
        setFdId(val);
        setMonth('');
    };

    const handleReset = () => {
        setBankId(''); setFdId(''); setYear(''); setMonth(''); setRanParams(null);
        dispatch(clearFDReport());
        dispatch(clearFDNumbers());
    };

    const runReport = () => {
        if (fdId === '') { toast.warning('Select an FD No.'); return; }
        if (bankId === '') { toast.warning('Select a Bank.'); return; }
        if (year === '') { toast.warning('Select a Year.'); return; }
        const monthNo = showMonth && month ? month : '0';
        const params = { FDRNo: selectedFdLabel, Year: year, Month: monthNo, Bank: selectedBankLabel };
        setRanParams(params);
        dispatch(fetchFDReport(params));
    };

    const handleRefresh = () => { if (ranParams) dispatch(fetchFDReport(ranParams)); };

    const rows = report?.lstFdDetails || [];
    const showBalances = ranParams && ranParams.Year !== 'Any';

    const totals = useMemo(() => rows.reduce((acc, r) => {
        acc.Credit += parseFloat(r.Credit) || 0;
        acc.Debit += parseFloat(r.Debit) || 0;
        acc.Interest += parseFloat(r.Interest) || 0;
        return acc;
    }, { Credit: 0, Debit: 0, Interest: 0 }), [rows]);

    const handleExport = () => {
        if (rows.length === 0) { toast.warning('No data to export'); return; }
        const exportRows = rows.map((r) => ({
            'FD Number': r.FDRNo, 'Transaction Date': r.PaymentDate, Bank: r.Bank, 'Mode Of Pay': r.ModeofPay,
            'Transaction No': r.No, REFUND: r.Credit, FD: r.Debit, Interest: r.Interest, 'Transaction Type': r.TransactionType,
        }));
        exportRows.push({ 'FD Number': '', 'Transaction Date': '', Bank: '', 'Mode Of Pay': '', 'Transaction No': 'TOTAL', REFUND: totals.Credit, FD: totals.Debit, Interest: totals.Interest, 'Transaction Type': '' });
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(exportRows), 'FD Report');
        XLSX.writeFile(wb, `FD_Report_${ranParams?.FDRNo || ''}_${new Date().toISOString().slice(0, 10)}.xlsx`);
        toast.success('Excel downloaded');
    };

    return (
        <div className="space-y-3 p-4 text-xs">
            {/* Header */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-3 border border-gray-200 dark:border-gray-700">
                <div className="flex items-start justify-between flex-wrap gap-3">
                    <div>
                        <h1 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
                            <div className="p-1.5 bg-gradient-to-br from-indigo-500 to-violet-600 rounded-lg">
                                <Landmark className="h-4 w-4 text-white" />
                            </div>
                            Fixed Deposit Report
                        </h1>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">Bank-wise fixed deposit transactions with opening/closing balance</p>
                        <nav className="flex items-center gap-1.5 text-[11px] text-gray-400 mt-1">
                            <span>Reports</span><ChevronRight className="w-3 h-3" /><span className="text-gray-700 dark:text-gray-200">FD Report</span>
                        </nav>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={handleRefresh} disabled={reportLoading || !ranParams} className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-medium transition-colors disabled:opacity-60">
                            <RefreshCw className={`h-3.5 w-3.5 ${reportLoading ? 'animate-spin' : ''}`} />Refresh
                        </button>
                        <button onClick={handleExport} disabled={rows.length === 0} className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-medium transition-colors disabled:opacity-60">
                            <Download className="h-3.5 w-3.5" />Export Excel
                        </button>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-3 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-1.5 mb-2">
                    <Filter className="h-3.5 w-3.5 text-indigo-600" />
                    <h2 className="text-xs font-semibold text-gray-700 dark:text-gray-300">Filters</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
                    <div>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Bank</label>
                        <select className={inputCls} value={bankId} onChange={(e) => handleBankChange(e.target.value)} disabled={bankListLoading}>
                            <option value="">-Please Select-</option>
                            {bankOptions.map((b) => <option key={b.Bank_Id} value={b.Bank_Id}>{b.Bank_Name}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">FD No</label>
                        <select className={inputCls} value={fdId} onChange={(e) => handleFdChange(e.target.value)} disabled={bankId === '' || fdNumbersLoading}>
                            <option value="">-Please Select-</option>
                            {fdOptions.map((f) => <option key={f.FdID} value={f.FdID}>{f.FdNo}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Year</label>
                        <select className={inputCls} value={year} onChange={(e) => setYear(e.target.value)} disabled={yearsLoading}>
                            <option value="">-Please Select-</option>
                            {yearOptions.map((y, i) => <option key={i} value={y.Year}>{y.YearValue}</option>)}
                        </select>
                    </div>
                    {showMonth && (
                        <div>
                            <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Month</label>
                            <select className={inputCls} value={month} onChange={(e) => setMonth(e.target.value)}>
                                <option value="">-Please Select-</option>
                                {MONTH_NAMES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                            </select>
                        </div>
                    )}
                    <div className="flex gap-2">
                        <button onClick={runReport} disabled={reportLoading} className="flex-1 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-semibold transition-colors disabled:opacity-60">View</button>
                        <button onClick={handleReset} className="flex-1 px-3 py-1.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg text-xs font-semibold transition-colors">Reset</button>
                    </div>
                </div>
            </div>

            {/* Report */}
            {reportLoading ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 flex items-center justify-center gap-3 py-10">
                    <Loader2 className="h-6 w-6 animate-spin text-indigo-600" /><span className="text-xs text-gray-500 dark:text-gray-400">Loading report…</span>
                </div>
            ) : reportError ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 flex items-center justify-center gap-3 py-10 text-red-500">
                    <AlertTriangle className="h-5 w-5" /><span className="text-xs">{reportError}</span>
                </div>
            ) : ranParams && rows.length === 0 && !(report?.OpeningBalance > 0) ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 py-10 text-center text-xs text-gray-400">No FD payments found.</div>
            ) : ranParams && (rows.length > 0 || report?.OpeningBalance > 0) ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700">
                        <h2 className="text-xs font-bold text-gray-700 dark:text-gray-300">FD Payments — {ranParams.FDRNo}</h2>
                    </div>
                    <div className="overflow-auto max-h-[70vh]">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gradient-to-r from-indigo-600 to-violet-600 sticky top-0 z-10">
                                <tr>
                                    {['FD Number', 'Transaction Date', 'Bank', 'Mode Of Pay', 'Transaction No', 'REFUND', 'FD', 'Interest', 'Transaction Type'].map((h) => (
                                        <th key={h} className="px-2 py-1.5 text-left text-[10px] font-bold text-white uppercase whitespace-nowrap">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                {showBalances && (
                                    <tr className="bg-indigo-50 dark:bg-indigo-900/20 font-semibold">
                                        <td colSpan={7} className="px-2 py-1 text-xs text-right text-gray-600 dark:text-gray-300">Opening Balance</td>
                                        <td className="px-2 py-1 text-xs text-right text-indigo-700 dark:text-indigo-400" colSpan={2}>{fmt(report.OpeningBalance)}</td>
                                    </tr>
                                )}
                                {rows.map((row, i) => (
                                    <tr key={i} className="hover:bg-indigo-50 dark:hover:bg-indigo-900/10 transition-colors">
                                        <td className="px-2 py-1 text-xs text-gray-800 dark:text-gray-100 whitespace-nowrap">{row.FDRNo}</td>
                                        <td className="px-2 py-1 text-xs text-gray-600 dark:text-gray-300 whitespace-nowrap">{row.PaymentDate}</td>
                                        <td className="px-2 py-1 text-xs text-gray-600 dark:text-gray-300 whitespace-nowrap">{row.Bank}</td>
                                        <td className="px-2 py-1 text-xs text-gray-600 dark:text-gray-300 whitespace-nowrap">{row.ModeofPay}</td>
                                        <td className="px-2 py-1 text-xs text-gray-600 dark:text-gray-300 whitespace-nowrap">{row.No}</td>
                                        <td className="px-2 py-1 text-xs text-right text-gray-700 dark:text-gray-200 whitespace-nowrap">{parseFloat(row.Credit) > 0 ? fmt(row.Credit) : ''}</td>
                                        <td className="px-2 py-1 text-xs text-right text-gray-700 dark:text-gray-200 whitespace-nowrap">{parseFloat(row.Debit) > 0 ? fmt(row.Debit) : ''}</td>
                                        <td className="px-2 py-1 text-xs text-right text-gray-700 dark:text-gray-200 whitespace-nowrap">{parseFloat(row.Interest) > 0 ? fmt(row.Interest) : ''}</td>
                                        <td className="px-2 py-1 text-xs text-gray-600 dark:text-gray-300 whitespace-nowrap">{row.TransactionType}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr className="bg-gray-100 dark:bg-gray-900/50 font-bold">
                                    <td className="px-2 py-1 text-xs" colSpan={5}>TOTAL</td>
                                    <td className="px-2 py-1 text-xs text-right">{fmt(totals.Credit)}</td>
                                    <td className="px-2 py-1 text-xs text-right">{fmt(totals.Debit)}</td>
                                    <td className="px-2 py-1 text-xs text-right">{fmt(totals.Interest)}</td>
                                    <td />
                                </tr>
                                {showBalances && (
                                    <tr className="bg-indigo-700/90 text-white font-bold">
                                        <td colSpan={7} className="px-2 py-1 text-xs text-right">Closing Balance</td>
                                        <td className="px-2 py-1 text-xs text-right" colSpan={2}>{fmt(report.ClosingBalance)}</td>
                                    </tr>
                                )}
                            </tfoot>
                        </table>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default FDReport;
